import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Product, ProductVariant } from '../TemplateInterface';
import { useEnhancedCart } from '@/contexts/EnhancedCartContext';
import { wholesaleConfigForCart, getEffectiveUnitPrice } from '@/utils/wholesalePricing';

interface ProductQuickViewProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  themeColors: { primary: string; secondary: string };
  formatPrice: (price: number) => string;
}

export function ProductQuickView({ open, onClose, product, themeColors, formatPrice }: ProductQuickViewProps) {
  const { addToCart } = useEnhancedCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (product) {
      setQuantity(1);
      setSelectedOptions({});
      setSelectedVariant(null);
      setCurrentImageIndex(0);
    }
  }, [product?.id]);

  useEffect(() => {
    if (product && product.variants && product.options) {
      const matchingVariant = product.variants.find(variant => {
        return Object.keys(selectedOptions).every(optionName => 
          variant.options[optionName] === selectedOptions[optionName]
        );
      });
      setSelectedVariant(matchingVariant || null);
    }
  }, [selectedOptions, product?.variants, product?.options]);

  const getCurrentPrice = () => {
    if (selectedVariant) {
      return selectedVariant.price;
    }
    return product!.price;
  };

  const getCurrentCompareAtPrice = () => {
    if (selectedVariant && selectedVariant.compareAtPrice) {
      return selectedVariant.compareAtPrice;
    }
    return product!.compareAtPrice;
  };

  const getCurrentStock = () => {
    if (selectedVariant && typeof selectedVariant.inventory === 'number') {
      return selectedVariant.inventory;
    }
    return Infinity;
  };

  const areAllRequiredOptionsSelected = () => {
    if (!product?.options) return true;
    return product.options.every(option => 
      !option.required || selectedOptions[option.name]
    );
  };

  const getMaxQuantity = () => {
    const stock = getCurrentStock();
    return stock === Infinity ? 999 : stock;
  };

  const handleAddToCart = () => {
    if (!product || !areAllRequiredOptionsSelected()) {
      return;
    }

    const cartItem = {
      ...product,
      price: getCurrentPrice(),
      compareAtPrice: getCurrentCompareAtPrice(),
      variantId: selectedVariant?.sku,
      options: selectedOptions
    };

    addToCart(
      cartItem, 
      quantity, 
      selectedOptions, 
      selectedVariant?.sku,
      selectedVariant ? `${Object.entries(selectedVariant.options).map(([key, value]) => `${key}: ${value}`).join(', ')}` : undefined,
      selectedVariant?.options
    );
    onClose();
  };

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value
    }));
  };

  if (!product) return null;

  const currentStock = getCurrentStock();
  const isOutOfStock = currentStock === 0;
  const maxQuantity = getMaxQuantity();
  const wsConfig = wholesaleConfigForCart(product, selectedVariant?.sku);
  const effectiveUnit = wsConfig
    ? getEffectiveUnitPrice(getCurrentPrice(), quantity, wsConfig)
    : getCurrentPrice();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden rounded-[1.75rem] border-black/5 sm:max-w-[800px]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-heading text-lg font-bold sm:text-xl">
            {product.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <div className="aspect-square overflow-hidden rounded-2xl bg-black/[0.03] md:aspect-auto">
                {product.images?.[currentImageIndex]?.url && (
                  <img
                    src={product.images[currentImageIndex].url}
                    alt={product.images[currentImageIndex].alt || product.name}
                    className="h-full w-full object-contain"
                  />
                )}
              </div>

              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 ${
                        currentImageIndex === index ? 'border-black/40' : 'border-black/10'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.alt || product.name}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xl font-semibold sm:text-2xl" style={{ color: themeColors.primary }}>
                  {formatPrice(getCurrentPrice())}
                </div>
                {getCurrentCompareAtPrice() && getCurrentCompareAtPrice()! > getCurrentPrice() && (
                  <div className="text-sm line-through text-gray-500">
                    {formatPrice(getCurrentCompareAtPrice()!)}
                  </div>
                )}
                {wsConfig && (
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    Wholesale: {formatPrice(wsConfig.tierPrice)} for {wsConfig.minQuantity}+ units.{' '}
                    {quantity >= wsConfig.minQuantity ? (
                      <span className="font-medium text-emerald-700">
                        Unit price: {formatPrice(effectiveUnit)}
                      </span>
                    ) : (
                      <span>
                        Add {wsConfig.minQuantity - quantity} more for wholesale unit pricing.
                      </span>
                    )}
                  </p>
                )}
              </div>

              {product.options && product.options.map((option) => (
                <div key={option.name} className="space-y-2">
                  <label className="text-sm font-medium">
                    {option.name}
                    {option.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <Select
                    value={selectedOptions[option.name] || ''}
                    onValueChange={(value) => handleOptionChange(option.name, value)}
                  >
                    <SelectTrigger className="h-11 w-full !rounded-full border-black/10">
                      <SelectValue placeholder={`Select ${option.name}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {option.values.map((value) => (
                        <SelectItem key={value.name} value={value.name}>
                          {value.name}
                          {value.price_adjustment !== 0 && (
                            <span className="ml-2 text-sm text-gray-500">
                              ({value.price_adjustment > 0 ? '+' : ''}{formatPrice(value.price_adjustment)})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              {product.description && (
                <div className="text-sm leading-relaxed whitespace-pre-line text-gray-600">
                  {product.description}
                </div>
              )}

              <div className="flex items-center gap-3 pt-4">
                <label className="text-sm font-medium">Quantity</label>
                <div className="flex items-center overflow-hidden rounded-full border border-black/10">
                  <button 
                    type="button"
                    className="flex h-9 w-9 items-center justify-center transition-colors hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-50" 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="h-9 w-12 border-0 text-center focus:outline-none"
                    value={quantity}
                    min={1}
                    max={maxQuantity}
                    onChange={(e) => {
                      const newQuantity = Math.max(1, Math.min(maxQuantity, parseInt(e.target.value) || 1));
                      setQuantity(newQuantity);
                    }}
                  />
                  <button 
                    type="button"
                    className="flex h-9 w-9 items-center justify-center transition-colors hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-50" 
                    onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                    disabled={quantity >= maxQuantity}
                  >
                    +
                  </button>
                </div>
                {maxQuantity < 999 && (
                  <span className="text-xs text-gray-500">Max: {maxQuantity}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-col border-t border-black/5 pt-4">
          <Button 
            className="mx-auto h-11 w-[80%] rounded-full text-base font-semibold text-white transition-all duration-200 hover:!text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50" 
            style={{ backgroundColor: themeColors.primary }} 
            onClick={handleAddToCart}
            disabled={isOutOfStock || !areAllRequiredOptionsSelected()}
          >
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </Button>
          
          {!areAllRequiredOptionsSelected() && (
            <p className="mt-2 text-center text-xs text-red-600">
              Kindly select all required options
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
